import {
  createChartFrame,
  resolveTitle,
  resolvePalette,
  clearSvg,
  getLayout,
  appendGrid,
  appendAxes,
  addLabel,
  renderLegend,
  createSvgEl,
  scaleLinear,
  toNumber,
  normalizeSeriesColors
} from './chart-utils.js';

const DEFAULT_SERIES = [
  { name: 'Alpha', values: [6, 4, 7, 5, 8] },
  { name: 'Beta', values: [3, 5, 4, 6, 4] }
];

export function render({ node }) {
  const { root, svg, legend, titleEl } = createChartFrame(node, 'Bar Chart');

  const update = (next) => {
    titleEl.textContent = resolveTitle(next, 'Bar Chart');
    const palette = resolvePalette(next);
    let series = normalizeSeriesColors(resolveSeries(next), palette);

    clearSvg(svg);
    const layout = getLayout();
    appendGrid(svg, layout);
    appendAxes(svg, layout);

    const labels = Array.isArray(next?.props?.labels || next?.data?.labels)
      ? (next.props?.labels || next.data?.labels)
      : null;

    const stacked = Boolean(next?.props?.stacked || next?.data?.stacked);
    const count = getCategoryCount(series);
    if (!count) {
      renderLegend(legend, series, { show: false });
      return;
    }

    const valuesByCat = collectValues(series, count, stacked);
    const bounds = computeBounds(valuesByCat, next);

    const slot = layout.inner.w / count;
    const gap = 0.18;
    const barBase = layout.inner.y + layout.inner.h;
    const groupWidth = slot * (1 - gap);
    const barWidth = stacked ? groupWidth : groupWidth / Math.max(1, series.length);

    for (let i = 0; i < count; i += 1) {
      const xStart = layout.inner.x + i * slot + slot * gap * 0.5;
      if (stacked) {
        let acc = 0;
        series.forEach((entry, sIdx) => {
          const value = toNumber(entry.values?.[i], 0);
          const y0 = scaleLinear(acc, bounds.yMin, bounds.yMax, barBase, layout.inner.y);
          acc += value;
          const y1 = scaleLinear(acc, bounds.yMin, bounds.yMax, barBase, layout.inner.y);
          const rect = createSvgEl('rect', {
            x: xStart,
            y: Math.min(y0, y1),
            width: barWidth,
            height: Math.abs(y1 - y0),
            fill: entry.color,
            'fill-opacity': 0.7
          });
          rect.classList.add('ew-chart-bar');
          svg.appendChild(rect);
        });
      } else {
        series.forEach((entry, sIdx) => {
          const value = toNumber(entry.values?.[i], 0);
          const y = scaleLinear(value, bounds.yMin, bounds.yMax, barBase, layout.inner.y);
          const rect = createSvgEl('rect', {
            x: xStart + sIdx * barWidth,
            y: Math.min(y, barBase),
            width: barWidth,
            height: Math.abs(barBase - y),
            fill: entry.color,
            'fill-opacity': 0.7
          });
          rect.classList.add('ew-chart-bar');
          svg.appendChild(rect);
        });
      }

      if (labels && labels[i]) {
        addLabel(svg, {
          x: xStart + groupWidth / 2,
          y: layout.inner.y + layout.inner.h + 14,
          text: labels[i],
          anchor: 'middle',
          className: 'ew-chart-label ew-chart-label-x'
        });
      }
    }

    addLabel(svg, {
      x: layout.inner.x,
      y: layout.inner.y - 6,
      text: formatValue(bounds.yMax),
      anchor: 'start'
    });
    addLabel(svg, {
      x: layout.inner.x,
      y: layout.inner.y + layout.inner.h + 14,
      text: formatValue(bounds.yMin),
      anchor: 'start'
    });

    renderLegend(legend, series, { show: next?.props?.legend !== false });
  };

  update(node);
  return { el: root, update };
}

function resolveSeries(node) {
  const raw = node?.data?.series ?? node?.props?.series;
  const values = node?.data?.values ?? node?.props?.values;

  if (Array.isArray(raw)) {
    if (raw.length && typeof raw[0] === 'number') {
      return [{ name: node?.props?.label || 'Series', values: raw }];
    }
    if (raw.length && raw[0] && Array.isArray(raw[0])) {
      return raw.map((entry, i) => ({ name: `Series ${i + 1}`, values: entry }));
    }
    if (raw.length && raw[0] && typeof raw[0] === 'object') {
      return raw.map((entry, i) => ({
        name: entry.name || `Series ${i + 1}`,
        color: entry.color,
        values: Array.isArray(entry.values) ? entry.values : []
      }));
    }
  }

  if (Array.isArray(values)) {
    return [{ name: node?.props?.label || 'Series', values }];
  }

  return DEFAULT_SERIES;
}

function getCategoryCount(series) {
  return series.reduce((max, entry) => Math.max(max, Array.isArray(entry.values) ? entry.values.length : 0), 0);
}

function collectValues(series, count, stacked) {
  const values = [];
  for (let i = 0; i < count; i += 1) {
    if (stacked) {
      let total = 0;
      series.forEach((entry) => {
        total += toNumber(entry.values?.[i], 0);
      });
      values.push(total);
    } else {
      series.forEach((entry) => {
        values.push(toNumber(entry.values?.[i], 0));
      });
    }
  }
  return values;
}

function computeBounds(values, node) {
  let yMin = Math.min(0, ...values);
  let yMax = Math.max(1, ...values);

  const minYOverride = toNumber(node?.props?.minY ?? node?.data?.minY, NaN);
  const maxYOverride = toNumber(node?.props?.maxY ?? node?.data?.maxY, NaN);

  if (Number.isFinite(minYOverride)) yMin = minYOverride;
  if (Number.isFinite(maxYOverride)) yMax = maxYOverride;

  if (yMin === yMax) yMax = yMin + 1;

  return { yMin, yMax };
}

function formatValue(value) {
  if (!Number.isFinite(value)) return '0';
  if (Math.abs(value) >= 1000) return value.toFixed(0);
  if (Math.abs(value) >= 100) return value.toFixed(1);
  return value.toFixed(2).replace(/\.00$/, '');
}
